import Clickable from 'components/Common/Clickable'
import Image from 'components/Common/Image'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import SolidInfoCircleIcon from 'components/Luxkit/Icons/solid/SolidInfoCircleIcon'
import HeadingTextBlock from 'components/Luxkit/TextBlocks/HeadingTextBlock'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import TourStyledProductTypeInfoModal, { TourStyledProductTypeInfoModalResult } from 'components/Tours/TourStyledProductTypeInfoModal'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { TOUR_STYLED_PRODUCT_TYPE_TITLES } from 'constants/tours'
import ModalContext from 'contexts/ModalContext'
import { rem } from 'polished'
import React, { MouseEventHandler, useCallback, useContext } from 'react'
import styled from 'styled-components'

interface TourStyleBenefit {
  image: App.Image
  title: string
  description: string
}

const TOUR_STYLES_BENEFITS: Record<App.Tours.V2ProductTypeStyled, [TourStyleBenefit, TourStyleBenefit, TourStyleBenefit]> = {
  classic_tour: [
    {
      image: { id: 'ogli9t02bmf7bpd3y01m' },
      title: 'Unmissable experiences',
      description: 'The best of every destination: curated activities and must-see highlights',
    },
    {
      image: { id: 'vohjh1k4mqb93gjbmnhb' },
      title: 'Premium accommodation',
      description: 'Carefully selected 4- and 4.5-star hotels for comfort and convenience throughout',
    },
    {
      image: { id: 'iko1j3uoook3lxd3wn0r' },
      title: 'Max 24 travellers',
      description: 'Small-group journeys with a maximum of 24 travellers',
    },
  ],
  deluxe_tour: [
    {
      image: { id: 'pt7qf3vv7r2r1vbkper' },
      title: 'Dedicated agent',
      description: 'Personalised support with a dedicated Tours Concierge agent',
    },
    {
      image: { id: '4c00aigy1blnhl4zy4g' },
      title: 'Five-star accommodation',
      description: 'Elevated five-star hotels to ensure enhanced comfort and quality',
    },
    {
      image: { id: 'iko1j3uoook3lxd3wn0r' },
      title: 'Max 16 travellers',
      description: 'A more personal and immersive experience at every destination with a maximum of 16 travellers',
    },
  ],
  ultra_lux_tour: [
    {
      image: { id: 'nwwdwffieplclruo8cmh' },
      title: 'Complimentary airport transfers',
      description: 'Commence your travel journey in comfort with a complimentary transfer from your home to your departing airport',
    },
    {
      image: { id: 'inm4w1t2o6fg9nyac9i' },
      title: 'World-class stays',
      description: 'The world\'s most prestigious hotels, featuring unparalleled five-star properties and global icons',
    },
    {
      image: { id: 'iko1j3uoook3lxd3wn0r' },
      title: 'Max 12 travellers',
      description: 'An intimate, exclusive journey with no more than 12 travellers, ensuring a personal and tailored experience',
    },
  ],
  signature_series_tour: [
    {
      image: { id: 'jsv70zstdiovyv92u68a' },
      title: 'Celebrity hosted',
      description: 'Ultra-exclusive journeys are designed and led by some of Australia’s most-loved personalities',
    },
    {
      image: { id: '3dlvhljdlpmxfjipck2p' },
      title: 'World-class stays',
      description: 'The world\'s most prestigious hotels, featuring unparalleled five-star properties and global icons',
    },
    {
      image: { id: 'io15a8zwv4o5agoksr' },
      title: 'One-off departure',
      description: 'Just one departure date, ensuring that every trip is a unique, once-in-a-lifetime experience',
    },
  ],
}

const BenefitTile = styled.div`
  display: grid;
  gap: ${rem(20)};
  grid-template: "content image" auto / minmax(0, 1fr) ${rem(68)};

  ${mediaQueryUp.tablet} {
    grid-template: "image content" auto / ${rem(68)} minmax(0, 1fr);
  }
`

const BenefitContent = styled(VerticalSpacer)`
  grid-area: content;
`

const BenefitImage = styled(Image)`
  grid-area: image;
`

interface Props {
  productType: App.Tours.V2ProductTypeStyled
  onCheckAvailability: () => void
}

function TourStyleKeyBenefitsSection({ productType, onCheckAvailability }: Props) {
  const showModal = useContext(ModalContext)
  const openTourStyleInfoModal = useCallback<MouseEventHandler<HTMLButtonElement>>(async(event) => {
    event.preventDefault()
    const result = await showModal<TourStyledProductTypeInfoModalResult>(<TourStyledProductTypeInfoModal
      primaryActionText="Check availability"
      productType={productType}
    />)
    if (result === 'primary-action') {
      onCheckAvailability()
    }
  }, [onCheckAvailability, showModal, productType])

  return <VerticalSpacer gap={24}>
    <HeadingTextBlock
      variant="heading4"
      endIcon={<Clickable onClick={openTourStyleInfoModal}><SolidInfoCircleIcon /></Clickable>}
    >
      Key benefits of {TOUR_STYLED_PRODUCT_TYPE_TITLES[productType]} Tours
    </HeadingTextBlock>
    <Group
      direction="vertical"
      gap={20}
      tabletGap={24}
      tabletDirection="horizontal"
      tabletHorizontalAlign="stretch"
    >
      {TOUR_STYLES_BENEFITS[productType].map((benefit, index) => <BenefitTile key={index}>
        <BenefitContent gap={4}>
          <Heading variant="heading6" colour="neutral-one">{benefit.title}</Heading>
          <BodyText variant="medium" colour="neutral-two">{benefit.description}</BodyText>
        </BenefitContent>
        <BenefitImage image={benefit.image} width={68} aspectRatio="1" dpr={2} />
      </BenefitTile>)}
    </Group>
  </VerticalSpacer>
}

export default TourStyleKeyBenefitsSection
